import {
  ComponentType,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactElement,
  SVGProps,
  useState,
} from 'react';
import { ChevronRightIcon } from '../../../assets/icons';
import { AppTo } from '../../../routes';
import { useIsDesktop } from '../../hooks';
import { tokens } from '../../theme';
import { AppLink } from '../AppLink/AppLink';

const MOBILE_ICON_SIZE = 24;
const DESKTOP_ICON_SIZE = 32;
// Padding fixo nos dois breakpoints no Figma do card "radio" (não fluido, diferente do padrão dos demais variants).
const RADIO_CARD_PADDING = '16px 24px';
const RADIO_MOBILE_ICON_GAP = 8;
const RADIO_DESKTOP_ICON_GAP = 16;
const RADIO_MOBILE_TEXT_GAP = 4;
const RADIO_DESKTOP_TEXT_GAP = 8;

type SelectableCardBaseProps = {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  isSelected?: boolean;
  onHover?: (active: boolean) => void;
};

export type SelectableCardProps =
  | (SelectableCardBaseProps & { variant: 'link'; to: AppTo })
  | (SelectableCardBaseProps & { variant: 'button'; onClick: () => void })
  | (SelectableCardBaseProps & {
      variant: 'radio';
      /** Padrão de teclado do papel `radio`: só o card selecionado (ou o primeiro, sem seleção) fica no fluxo de Tab. */
      tabIndex?: number;
      onSelect: () => void;
    });

function SelectableCardComponent(
  props: SelectableCardProps,
  ref: ForwardedRef<HTMLButtonElement>,
): ReactElement {
  const { icon: Icon, title, description, isSelected = false, onHover } = props;
  const isDesktop = useIsDesktop();
  const iconSize = isDesktop ? DESKTOP_ICON_SIZE : MOBILE_ICON_SIZE;
  const isRadio = props.variant === 'radio';
  const [isHovered, setIsHovered] = useState(false);
  // Num grupo radio, destacar no hover sugeriria seleção em dois itens ao mesmo tempo — só o estado real (isSelected) reflete no visual.
  const highlighted = isSelected || (!isRadio && isHovered);

  const cardStyle: CSSProperties = isRadio
    ? {
        ...styles.card,
        ...styles.radioCard,
        gap: isDesktop ? RADIO_DESKTOP_ICON_GAP : RADIO_MOBILE_ICON_GAP,
        padding: RADIO_CARD_PADDING,
        backgroundColor: isSelected ? tokens.colors.brand.red.light950 : tokens.colors.global.white,
        borderColor: isSelected
          ? tokens.colors.brand.red.DEFAULT
          : tokens.colors.brand.gray.light850,
      }
    : {
        ...styles.card,
        ...(highlighted ? styles.cardSelected : styles.cardNotSelected),
        flexDirection: isDesktop ? 'column' : 'row',
        alignItems: isDesktop ? 'flex-start' : 'center',
        justifyContent: isDesktop ? 'flex-start' : 'space-between',
      };

  const hoverHandlers = {
    onMouseOver: () => {
      setIsHovered(true);
      onHover?.(true);
    },
    onMouseOut: () => {
      setIsHovered(false);
      onHover?.(false);
    },
    onFocus: () => {
      setIsHovered(true);
      onHover?.(true);
    },
    onBlur: () => {
      setIsHovered(false);
      onHover?.(false);
    },
  };

  const content = isRadio ? (
    <>
      <Icon width={iconSize} height={iconSize} aria-hidden="true" focusable="false" />
      <div
        style={{
          ...styles.radioInfo,
          gap: isDesktop ? RADIO_DESKTOP_TEXT_GAP : RADIO_MOBILE_TEXT_GAP,
        }}
      >
        <p style={styles.radioTitle}>{title}</p>
        <p style={styles.radioDescription}>{description}</p>
      </div>
    </>
  ) : (
    <>
      <div style={isDesktop ? styles.iconAndTextDesktop : styles.iconAndTextMobile}>
        <Icon width={iconSize} height={iconSize} aria-hidden="true" focusable="false" />
        <div
          style={{
            ...styles.textContainer,
            gap: isDesktop ? tokens.spacing.small : tokens.spacing.none,
          }}
        >
          <p style={styles.title}>{title}</p>
          <p
            style={{
              ...styles.description,
              color: isDesktop
                ? tokens.colors.brand.gray.DEFAULT
                : tokens.colors.brand.gray.light150,
            }}
          >
            {description}
          </p>
        </div>
      </div>
      {!isDesktop && <ChevronRightIcon aria-hidden="true" focusable="false" />}
    </>
  );

  if (props.variant === 'radio') {
    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={isSelected}
        tabIndex={props.tabIndex}
        onClick={props.onSelect}
        style={{ ...cardStyle, ...styles.buttonStyle }}
      >
        {content}
      </button>
    );
  }

  if (props.variant === 'button') {
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={isSelected}
        onClick={props.onClick}
        style={{ ...cardStyle, ...styles.buttonStyle }}
        {...hoverHandlers}
      >
        {content}
      </button>
    );
  }

  return (
    <AppLink
      to={props.to}
      aria-current={isSelected ? 'true' : undefined}
      style={cardStyle}
      {...hoverHandlers}
    >
      {content}
    </AppLink>
  );
}

export const SelectableCard = forwardRef(SelectableCardComponent);

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: 'flex',
    gap: tokens.spacing.small,
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${tokens.colors.brand.gray.light850}`,
    borderRadius: tokens.radius.small,
    padding: tokens.spacing.cardPadding,
    textDecoration: 'none',
  },
  buttonStyle: {
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  cardNotSelected: {
    backgroundColor: tokens.colors.global.white,
  },
  cardSelected: {
    backgroundColor: tokens.colors.brand.red.light850,
  },
  radioCard: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    minWidth: 0,
  },
  iconAndTextMobile: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacing.small,
    minWidth: 0,
  },
  iconAndTextDesktop: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.cardIconGap,
    width: '100%',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  radioInfo: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  title: {
    margin: 0,
    fontSize: tokens.typography.fontSize.title,
    fontWeight: tokens.typography.fontWeight.medium,
    lineHeight: tokens.typography.lineHeight.body,
    color: tokens.colors.global.black,
  },
  description: {
    margin: 0,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.regular,
    lineHeight: tokens.typography.lineHeight.body,
  },
  radioTitle: {
    margin: 0,
    fontSize: tokens.typography.fontSize.title,
    fontWeight: tokens.typography.fontWeight.medium,
    lineHeight: tokens.typography.lineHeight.heading,
    color: tokens.colors.global.black,
  },
  radioDescription: {
    margin: 0,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.regular,
    lineHeight: tokens.typography.lineHeight.body,
    color: tokens.colors.brand.gray.DEFAULT,
  },
};
